import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, pdfUrl, groomingPdfUrl, boardingPdfUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { brandName, money } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Phone, Mail, MapPin, PawPrint, Plus, Scissors, Home, FilePlus2, Download, Pencil, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value }) => (
  <div className="text-center px-4 py-2">
    <div className="font-primary text-2xl font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

function AddPetDialog({ clientId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ pet_name: "", species: "Dog", breed: "", sex: "", dob: "", weight: "", colour: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/pets`, f);
      toast.success(`${f.pet_name} added`);
      setOpen(false);
      setF({ pet_name: "", species: "Dog", breed: "", sex: "", dob: "", weight: "", colour: "" });
      onAdded();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-pet-btn"><Plus className="h-4 w-4 mr-1" />Add Pet</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add a Pet</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Pet Name *</Label><Input value={f.pet_name} onChange={(e) => setF({ ...f, pet_name: e.target.value })} required data-testid="pet-name" /></div>
            <div className="space-y-1.5"><Label>Species *</Label><Input value={f.species} onChange={(e) => setF({ ...f, species: e.target.value })} required data-testid="pet-species" /></div>
            <div className="space-y-1.5"><Label>Breed</Label><Input value={f.breed} onChange={(e) => setF({ ...f, breed: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Sex</Label><Input value={f.sex} onChange={(e) => setF({ ...f, sex: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Date of Birth</Label><Input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Weight</Label><Input value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="save-pet-btn" className="w-full">{busy ? "Saving…" : "Add Pet"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canService = user?.role === "admin" || user?.role === "staff";
  const canVet = user?.brand !== "kennelmart"; // barkode staff/doctor + admin
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setData(data);
    } catch (e) { toast.error("Could not load client"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8">Loading…</div>;
  const { client, pets, consultations, grooming, boarding, appointments } = data;
  const vetHidden = client.brand === "kennelmart";

  const StatusPill = ({ s }) => <span className={`text-xs px-2 py-0.5 rounded-full ${s === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{s}</span>;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-6 mb-6" data-testid="client-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-primary text-3xl font-bold tracking-tight">{client.owner_name}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{client.phone || "—"}</span>
              {client.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{client.email}</span>}
              {client.address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{client.address}</span>}
              <span>{brandName(client.brand)}{client.owning_branch ? ` · ${client.owning_branch}` : ""}</span>
            </div>
          </div>
          <div className="flex divide-x divide-border/60 rounded-xl border border-border/60 bg-card/60">
            <Stat label="Pets" value={client.pets_count} />
            <Stat label="Orders" value={client.orders} />
            {user?.role === "admin" && <Stat label="AOV" value={money(client.aov)} />}
            {user?.role === "admin" && <Stat label="Spend" value={money(client.total_spend)} />}
          </div>
        </div>
      </div>

      {/* Pets */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-primary text-xl font-semibold">Pets</h2>
        <AddPetDialog clientId={id} onAdded={load} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {pets.length === 0 && <Card className="p-8 text-center text-muted-foreground col-span-full"><PawPrint className="h-8 w-8 mx-auto opacity-30 mb-2" />No pets yet. Add one to start booking services.</Card>}
        {pets.map((p) => (
          <Card key={p.id} className="p-4 border-border/60" data-testid={`client-pet-${p.id}`}>
            <div className="flex items-center gap-3">
              {p.photo_file_id ? <img src={fileUrl(p.photo_file_id)} alt={p.pet_name} className="w-12 h-12 rounded-lg object-cover border border-border" />
                : <div className="w-12 h-12 rounded-lg bg-muted border border-border grid place-items-center"><PawPrint className="h-5 w-5 text-muted-foreground" /></div>}
              <div className="min-w-0 flex-1">
                <Link to={`/patients/${p.id}`} className="font-medium hover:underline truncate block">{p.pet_name}</Link>
                <div className="text-xs text-muted-foreground truncate">{p.species}{p.breed ? ` · ${p.breed}` : ""} · {p.case_number}</div>
              </div>
            </div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {canVet && !vetHidden && <Link to={`/consultations/new?patient=${p.id}`}><Button size="sm" variant="outline" data-testid={`pet-vet-${p.id}`}><FilePlus2 className="h-3.5 w-3.5 mr-1" />Vet</Button></Link>}
              {canService && <Link to={`/grooming/new?patient=${p.id}`}><Button size="sm" variant="outline"><Scissors className="h-3.5 w-3.5 mr-1" />Groom</Button></Link>}
              {canService && <Link to={`/boarding/new?patient=${p.id}`}><Button size="sm" variant="outline"><Home className="h-3.5 w-3.5 mr-1" />Board</Button></Link>}
            </div>
          </Card>
        ))}
      </div>

      {/* Aggregated history */}
      <Tabs defaultValue={vetHidden ? "grooming" : "vet"}>
        <TabsList className="mb-4">
          {!vetHidden && <TabsTrigger value="vet" data-testid="tab-vet">Vet Consultation ({consultations.length})</TabsTrigger>}
          <TabsTrigger value="grooming" data-testid="tab-grooming">Grooming ({grooming.length})</TabsTrigger>
          <TabsTrigger value="boarding" data-testid="tab-boarding">Boarding ({boarding.length})</TabsTrigger>
          <TabsTrigger value="appts" data-testid="tab-appts">Appointments ({appointments.length})</TabsTrigger>
        </TabsList>

        {!vetHidden && (
          <TabsContent value="vet" className="space-y-3">
            {consultations.length === 0 && <Card className="p-8 text-center text-muted-foreground">No consultations yet.</Card>}
            {consultations.map((c) => (
              <Card key={c.id} className="p-4 border-border/60 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(c.created_at).toLocaleDateString()} · {c.pet_name}</div>
                  <div className="font-medium text-sm">{c.primary_diagnosis || "Consultation"} · {c.amount ? money(c.amount) : "Not set"}</div>
                </div>
                <div className="flex gap-2">
                  <a href={pdfUrl(c.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="h-4 w-4" /></Button></a>
                  <Link to={`/consultations/${c.id}/edit`}><Button size="sm" variant="ghost"><Pencil className="h-4 w-4" /></Button></Link>
                </div>
              </Card>
            ))}
          </TabsContent>
        )}

        <TabsContent value="grooming" className="space-y-3">
          {grooming.length === 0 && <Card className="p-8 text-center text-muted-foreground">No grooming bookings.</Card>}
          {grooming.map((g) => (
            <Card key={g.id} className="p-4 border-border/60 flex items-center justify-between gap-3">
              <Link to={`/grooming/${g.id}/edit`} className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(g.created_at).toLocaleDateString()} · {g.pet_name} · {g.branch}</div>
                <div className="font-medium text-sm">Grooming · {money(g.amount_paid)}</div>
              </Link>
              <div className="flex items-center gap-2"><StatusPill s={g.status} /><a href={groomingPdfUrl(g.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="h-4 w-4" /></Button></a></div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="boarding" className="space-y-3">
          {boarding.length === 0 && <Card className="p-8 text-center text-muted-foreground">No boarding bookings.</Card>}
          {boarding.map((b) => (
            <Card key={b.id} className="p-4 border-border/60 flex items-center justify-between gap-3">
              <Link to={`/boarding/${b.id}/edit`} className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(b.created_at).toLocaleDateString()} · {b.pet_name} · {b.branch}</div>
                <div className="font-medium text-sm">{b.boarding_type === "overnight" ? "Overnight" : "Day"} Boarding · {money(b.amount_paid)}</div>
              </Link>
              <div className="flex items-center gap-2"><StatusPill s={b.status} /><a href={boardingPdfUrl(b.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="h-4 w-4" /></Button></a></div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="appts" className="space-y-3">
          {appointments.length === 0 && <Card className="p-8 text-center text-muted-foreground">No appointments.</Card>}
          {appointments.map((a) => (
            <Card key={a.id} className="p-4 border-border/60 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />{new Date(a.date_time).toLocaleString()} · {a.pet_name}</div>
                <div className="font-medium text-sm capitalize">{a.service_type || a.reason || "Appointment"}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${a.status === "cancelled" ? "bg-destructive/15 text-destructive" : a.status === "confirmed" ? "bg-emerald-500/15 text-emerald-600" : a.status === "rescheduled" ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground"}`}>{a.status || "scheduled"}</span>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
